import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Toast from '../components/ui/Toast';
import TicketPackageSelector from '../components/events/TicketPackageSelector';
import {
  formatTimeRange,
  formatKesPrice,
  formatDisplayDate,
  getEventDisplay,
  getEventDateBadgeParts,
  isMultiDayEvent,
  formatMultiDayRangeLabel,
  isEventSalesClosed,
  isEventPast,
  getEventSalesClosedReason,
  getEventLocationDetail,
  isVirtualVenue,
  getEventHostDisplay,
  getEventShortAbsoluteUrl,
  getEventShortPath,
  sortPricingTiersByPrice,
  getPublicPricingTiers,
  buildEventShareHighlight,
  getStartingOriginalPrice
} from '../utils/eventFormat';
import {
  generateBrandedQrDataUrl,
  QR_SIZE_DISPLAY,
  QR_SIZE_DOWNLOAD
} from '../utils/brandedQr';
import config from '../config/env';
import { resolveMediaUrl, getEventImageUrl } from '../utils/mediaUrl';
import SiteHeader from '../components/layout/SiteHeader';
import PageBreadcrumb from '../components/layout/PageBreadcrumb';
import SiteLayout, { SiteMain } from '../components/layout/SiteLayout';
import EventDetailsSkeleton from '../components/events/EventDetailsSkeleton';
import usePageMeta, { absoluteMediaUrl, absoluteSiteUrl } from '../hooks/usePageMeta';

const EventDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ticketQuantity, setTicketQuantity] = useState(1);
  const [tierQuantities, setTierQuantities] = useState({});
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [toast, setToast] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [eventQrDataUrl, setEventQrDataUrl] = useState('');

  // Fetch event details
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const apiBase = (config.apiUrl || 'http://localhost:5000').replace(/\/$/, '');
        const response = await fetch(`${apiBase}/api/events/${id}`);
        const data = await response.json();

        if (response.ok && data.success) {
          if (data.data.event.pricingTiers && data.data.event.pricingTiers.length > 0) {
            const sorted = sortPricingTiersByPrice(
              getPublicPricingTiers(data.data.event.pricingTiers),
              data.data.event.price
            );
            setSelectedPricing(sorted[0]);
            setEvent({ ...data.data.event, pricingTiers: sorted });
          } else {
            setEvent(data.data.event);
          }
        } else if (
          response.status === 403 &&
          (data.status === 'CANCELLED' || data.message?.toLowerCase().includes('cancelled'))
        ) {
          setError('CANCELLED');
        } else {
          setError(data.message || 'Event not found');
        }
      } catch (err) {
        setError('Failed to load event details');
        console.error('Error fetching event:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-KE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-KE', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(parseFloat(price));
  };

  const getPriceRange = (pricingTiers) => {
    if (!pricingTiers || pricingTiers.length === 0) return null;
    const prices = pricingTiers.map(tier => parseFloat(tier.price));
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max ? formatPrice(min) : `${formatPrice(min)} - ${formatPrice(max)}`;
  };

  const handleTierQuantityChange = (tierId, quantity) => {
    setTierQuantities((prev) => ({ ...prev, [tierId]: quantity }));
  };

  const getCartItems = () => {
    if (!event?.pricingTiers?.length) return [];
    return event.pricingTiers
      .map((tier) => ({
        tierId: tier.id,
        name: tier.name,
        price: Number(tier.price),
        quantity: tierQuantities[tier.id] || 0
      }))
      .filter((item) => item.quantity > 0);
  };

  const getOrderTotal = () => {
    const items = getCartItems();
    if (items.length) {
      return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    }
    return Number(selectedPricing?.price || event?.price || 0) * ticketQuantity;
  };

  const getOrderQuantity = () => {
    const items = getCartItems();
    if (items.length) {
      return items.reduce((sum, item) => sum + item.quantity, 0);
    }
    return ticketQuantity;
  };

  const handleTicketPurchase = async () => {
    if (event.status === 'SOLD_OUT') {
      setToast({
        message: '❌ This event is sold out. Tickets are no longer available.',
        type: 'error'
      });
      return;
    }

    if (isEventSalesClosed(event)) {
      setToast({
        message: isEventPast(event)
          ? '❌ This event has ended. Ticket sales are no longer available.'
          : '❌ Tickets are not available for this event.',
        type: 'error'
      });
      return;
    }

    setPurchasing(true);
    try {
      const cartItems = getCartItems();
      const hasPackages = event.pricingTiers?.length > 0;

      if (hasPackages && cartItems.length === 0) {
        setToast({
          message: 'Please select at least one ticket package.',
          type: 'error'
        });
        return;
      }

      const total = getOrderTotal();
      const quantity = getOrderQuantity();

      if (quantity < 1) {
        setToast({ message: 'Please select at least one ticket.', type: 'error' });
        return;
      }

      if (total <= 0) {
        setToast({
          message: 'Please select a ticket package with a valid price.',
          type: 'error'
        });
        return;
      }

      const purchaseParams = new URLSearchParams({
        event_id: event.id,
        quantity: String(quantity),
        total: String(total)
      });

      if (cartItems.length > 0) {
        purchaseParams.set('cart', JSON.stringify(cartItems));
      } else {
        purchaseParams.set('price', String(selectedPricing?.price || event.price));
        purchaseParams.set('ticket_type', selectedPricing?.id || 'regular');
      }

      navigate(`/checkout?${purchaseParams.toString()}`);
    } catch (err) {
      setToast({
        message: '❌ Failed to proceed to purchase. Please try again.',
        type: 'error'
      });
    } finally {
      setPurchasing(false);
    }
  };

  const handleSocialShare = (platform) => {
    const url = getEventShortAbsoluteUrl(event.id);
    const title = event?.title || 'Check out this event!';
    const text = `Check out this amazing event: ${title}`;
    
    let shareUrl = '';
    
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowShareModal(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(getEventShortAbsoluteUrl(event.id));
    setToast({
      message: 'Event link copied',
      type: 'success'
    });
    setShowShareModal(false);
  };

  const downloadQrCode = async () => {
    if (!shortEventUrl || !event) return;
    try {
      const hiRes = await generateBrandedQrDataUrl(shortEventUrl, {
        size: QR_SIZE_DOWNLOAD
      });
      const link = document.createElement('a');
      link.href = hiRes;
      link.download = `${event.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'event'}-qr.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('QR download failed:', err);
    }
  };

  const display = event ? getEventDisplay(event) : null;
  const hasPackages = event?.pricingTiers?.length > 0;
  const dateBadge = event ? getEventDateBadgeParts(event.date) : null;
  const multiDayRange = event ? formatMultiDayRangeLabel(event.date, event.endDate) : null;
  const isMultiDay = event ? isMultiDayEvent(event.date, event.endDate) : false;
  const timeRange = event
    ? (display?.timeRange || formatTimeRange(event.startTime, event.endTime))
    : null;
  const salesClosed = event ? isEventSalesClosed(event) : false;
  const isPastEvent = event ? isEventPast(event) : false;
  const salesReason = event ? getEventSalesClosedReason(event) : null;
  const lastEventDayLabel = event
    ? formatDisplayDate(event.endDate || event.date)
    : null;
  const locationDetail = event ? getEventLocationDetail(event) : null;
  const hostDisplay = event ? getEventHostDisplay(event) : null;
  const shortEventUrl = event ? (event.shortUrl || getEventShortAbsoluteUrl(event.id)) : '';

  useEffect(() => {
    let active = true;

    const generateEventQr = async () => {
      if (!shortEventUrl) {
        setEventQrDataUrl('');
        return;
      }

      try {
        const dataUrl = await generateBrandedQrDataUrl(shortEventUrl, {
          size: QR_SIZE_DISPLAY
        });

        if (active) setEventQrDataUrl(dataUrl);
      } catch (err) {
        console.error('Error generating event QR code:', err);
        if (active) setEventQrDataUrl('');
      }
    };

    generateEventQr();

    return () => {
      active = false;
    };
  }, [shortEventUrl]);

  const seoStatus = event
    ? event.isPast
      ? 'Event ended'
      : event.salesClosed || event.status === 'SOLD_OUT'
        ? 'Sold out'
        : null
    : null;
  const seoPackages = event?.pricingTiers?.length
    ? sortPricingTiersByPrice(getPublicPricingTiers(event.pricingTiers), event.price)
        .filter((t) => t && t.isActive !== false)
        .slice(0, 6)
        .map((t) => `${t.name} ${formatKesPrice(t.price)}`)
        .join(' · ')
    : event?.startingPrice != null
      ? `From ${formatKesPrice(event.startingPrice)}`
      : null;
  const seoDescription = event
    ? [
        event.display?.dateRange || formatDisplayDate(event.date),
        event.display?.timeRange,
        locationDetail?.label || event.venue?.name,
        seoPackages,
        seoStatus,
        buildEventShareHighlight(event)
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  usePageMeta({
    title: event
      ? `${event.title}${locationDetail?.label || event.venue?.name ? ` · ${locationDetail?.label || event.venue?.name}` : ''}`
      : undefined,
    description: event ? seoDescription : undefined,
    // Always attach the event poster as the share / OG image
    image: event ? absoluteMediaUrl(event.imageUrl || event.poster) : undefined,
    imageAlt: event ? `${event.title} event poster` : undefined,
    url: event ? absoluteSiteUrl(getEventShortPath(event.id)) : undefined,
    keywords: event
      ? [
          event.title,
          'tickets',
          event.venue?.city,
          ...(event.categories || []).map((c) => c.name),
          event.category?.name,
          ...getPublicPricingTiers(event.pricingTiers).map((t) => t.name)
        ]
          .filter(Boolean)
          .slice(0, 10)
          .join(', ')
      : undefined
  });

  const pageShell = (content) => (
    <SiteLayout className="bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
      <SiteHeader />
      <SiteMain className="flex-1 flex items-center justify-center px-4 py-16">
        {content}
      </SiteMain>
    </SiteLayout>
  );

  if (loading) {
    return (
      <SiteLayout className="bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
        <SiteHeader />
        <SiteMain>
          <EventDetailsSkeleton />
        </SiteMain>
      </SiteLayout>
    );
  }

  if (error === 'CANCELLED') {
    return pageShell(
      <div className="text-center max-w-lg">
        <div className="text-8xl mb-6">🚫</div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Event Cancelled</h2>
        <p className="text-gray-600 mb-8 text-lg">This event has been cancelled and is no longer available for viewing.</p>
        <Link to="/events" className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8 py-4 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl">
          Back to Events
        </Link>
      </div>
    );
  }

  if (error || !event) {
    return pageShell(
      <div className="text-center max-w-lg">
        <div className="text-8xl mb-6">😔</div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Event Not Found</h2>
        <p className="text-gray-600 mb-8 text-lg">{error || 'The event you are looking for does not exist.'}</p>
        <Link to="/events" className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8 py-4 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl">
          Back to Events
        </Link>
      </div>
    );
  }

  return (
    <SiteLayout className="bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 overflow-x-hidden max-w-[100vw]">
      <SiteHeader />

      <SiteMain className="flex-1 w-full min-w-0 overflow-x-hidden">
      <PageBreadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: 'Events', to: '/events' },
          { label: event.title }
        ]}
      />

      {/* Event Details */}
      <div className="container mx-auto px-4 py-6 lg:py-8 max-w-full overflow-x-hidden">
        <div className="max-w-5xl mx-auto w-full min-w-0">
        <div className="grid lg:grid-cols-12 gap-5 lg:gap-6 min-w-0 items-start">
          {/* Left - poster (+ about/host on desktop only), always top-aligned */}
          <div className="lg:col-span-5 min-w-0 max-w-full self-start">
            <div className="bg-white rounded-xl shadow-md border border-amber-100 overflow-hidden lg:sticky lg:top-24 w-full max-w-full self-start">
              {/* Mobile: capped height. Desktop: full-width portrait poster, top-anchored card. */}
              <div
                className="relative w-full max-w-full overflow-hidden bg-gray-100
                  h-[min(48vh,360px)] sm:h-[min(52vh,400px)]
                  lg:h-auto lg:aspect-[3/4] lg:max-h-[min(70vh,560px)]"
              >
                {event.imageUrl ? (
                  <img
                    src={resolveMediaUrl(event.imageUrl)}
                    alt={event.title}
                    onError={(e) => { e.currentTarget.src = getEventImageUrl(''); }}
                    className={`event-details-poster ${isPastEvent ? 'opacity-90' : ''}`}
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <span className="text-6xl text-white">🎪</span>
                  </div>
                )}
                {(event.status === 'SOLD_OUT' || event.status === 'CANCELLED' || isPastEvent) && (
                  <div className="absolute top-3 left-3 z-10">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-md shadow ${
                      isPastEvent
                        ? 'bg-gray-800/90 text-white'
                        : event.status === 'SOLD_OUT'
                          ? 'bg-red-500 text-white'
                          : 'bg-gray-500 text-white'
                    }`}>
                      {isPastEvent ? 'ENDED' : event.status === 'SOLD_OUT' ? 'SOLD OUT' : 'CANCELLED'}
                    </span>
                  </div>
                )}
              </div>

              {/* About + host under poster - desktop only */}
              <div className="hidden lg:block p-4 lg:p-5">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">About</h2>
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                  {event.description || 'No description available.'}
                </p>

                {hostDisplay?.name && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Hosted by</p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-gradient-to-r from-amber-500 to-orange-500 rounded-md flex items-center justify-center text-white font-semibold text-sm shrink-0">
                        {hostDisplay.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{hostDisplay.name}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right - metadata, tickets, extras */}
          <div className="lg:col-span-7 space-y-4 min-w-0 max-w-full">
            {/* Event metadata */}
            <div className="bg-white rounded-xl shadow-md border border-amber-100 p-4 lg:p-5">
              <div className="flex gap-4 mb-4">
                {dateBadge && (
                  <div
                    className={`shrink-0 w-[4.25rem] rounded-xl border-2 text-center py-2.5 px-1 shadow-sm ${
                      isPastEvent
                        ? 'border-gray-200 bg-gradient-to-b from-gray-50 to-white'
                        : 'border-amber-200/80 bg-gradient-to-b from-amber-50 via-white to-orange-50/40'
                    }`}
                    aria-label={`Event date: ${dateBadge.weekday} ${dateBadge.day} ${dateBadge.month} ${dateBadge.year}`}
                  >
                    <p className={`text-[10px] font-bold tracking-widest leading-none ${
                      isPastEvent ? 'text-gray-500' : 'text-amber-600'
                    }`}>
                      {dateBadge.weekday}
                    </p>
                    <p className={`text-2xl font-bold leading-none my-1 tabular-nums ${
                      isPastEvent ? 'text-gray-600' : 'text-gray-900'
                    }`}>
                      {dateBadge.day}
                    </p>
                    <p className={`text-[10px] font-bold tracking-widest leading-none ${
                      isPastEvent ? 'text-gray-500' : 'text-amber-600'
                    }`}>
                      {dateBadge.month}
                    </p>
                    <p className={`text-[10px] font-semibold tracking-wide leading-none mt-1 ${
                      isPastEvent ? 'text-gray-500' : 'text-gray-700'
                    }`}>
                      {dateBadge.year}
                    </p>
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      {(event.categories?.length
                        ? event.categories
                        : (event.category ? [event.category] : [])
                      ).map((cat) => (
                        <span
                          key={cat.id || cat.name}
                          className="text-xs font-semibold px-2.5 py-1 rounded-md bg-amber-100 text-amber-800 border border-amber-200"
                        >
                          {cat.name}
                        </span>
                      ))}
                      {!event.categories?.length && !event.category && (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-amber-100 text-amber-800 border border-amber-200">
                          General
                        </span>
                      )}
                      {locationDetail?.label && !isVirtualVenue(event.venue) && event.venue?.city && (
                        <span className="text-xs text-gray-500">{event.venue.city}</span>
                      )}
                      {locationDetail?.label && isVirtualVenue(event.venue) && (
                        <span className="text-xs text-gray-500">Virtual</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right">
                        {!hasPackages ? (
                          <>
                            <span className="text-lg font-bold text-amber-600">{formatPrice(event.price)}</span>
                            <span className="text-xs text-gray-500 ml-1">/ ticket</span>
                          </>
                        ) : (
                          <>
                            <span className="text-xs text-gray-500">From </span>
                            <span className="text-lg font-bold text-amber-600">
                              {(event.startingOriginalPrice
                                ?? getStartingOriginalPrice(event)) != null && (
                                <span className="block text-xs font-medium text-gray-400 line-through leading-none mb-0.5">
                                  {formatKesPrice(
                                    event.startingOriginalPrice ?? getStartingOriginalPrice(event)
                                  )}
                                </span>
                              )}
                              {formatKesPrice(event.startingPrice ?? event.price)}
                            </span>
                          </>
                        )}
                      </div>
                      <button
                        onClick={() => setShowShareModal(true)}
                        className="shrink-0 p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-amber-300 transition-colors"
                        aria-label="Share event"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <h1 className="text-xl lg:text-2xl font-bold text-gray-900 leading-snug mt-2">
                    {event.title}
                  </h1>
                </div>
              </div>

              <div className="space-y-2.5">
                {(isMultiDay || timeRange) && (
                  <div className="flex items-start gap-2.5 text-sm">
                    <svg className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {isMultiDay ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      )}
                    </svg>
                    <p className="text-gray-700 leading-snug">
                      {multiDayRange && (
                        <>
                          <span className="text-gray-500">From </span>
                          <span className="font-medium text-gray-900">{multiDayRange.from}</span>
                          <span className="text-amber-400 mx-1.5">→</span>
                          <span className="text-gray-500">to </span>
                          <span className="font-medium text-gray-900">{multiDayRange.to}</span>
                        </>
                      )}
                      {multiDayRange && timeRange && (
                        <span className="text-gray-300 mx-2" aria-hidden="true">·</span>
                      )}
                      {timeRange && (
                        <span className={multiDayRange ? 'text-gray-600' : 'text-gray-700'}>
                          {timeRange}
                        </span>
                      )}
                    </p>
                  </div>
                )}

                {locationDetail?.label && (
                  <div className="flex items-start gap-2.5 text-sm">
                    <svg className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <p className="font-medium text-gray-900">{locationDetail.label}</p>
                      {locationDetail.sublabel && (
                        <p className="text-gray-500 text-xs mt-0.5">{locationDetail.sublabel}</p>
                      )}
                      {locationDetail.url && (
                        <a
                          href={locationDetail.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-amber-700 hover:text-amber-800 font-medium mt-1 inline-block"
                        >
                          Join online →
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {hostDisplay?.name && (
                  <p className="hidden lg:block text-[11px] text-gray-500 mt-1 normal-case tracking-normal">
                    Hosted by <span className="text-gray-600">{hostDisplay.name}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Tickets */}
            <div className="bg-white rounded-xl shadow-md border border-amber-100 p-3 lg:p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-2">
                {salesClosed ? 'Tickets' : 'Get Your Tickets'}
              </h3>

              {salesClosed && (
                <div className={`rounded-lg border p-4 mb-4 ${
                  isPastEvent
                    ? 'bg-gray-50 border-gray-200'
                    : 'bg-red-50 border-red-100'
                }`}>
                  <div className="flex gap-3">
                    <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${
                      isPastEvent ? 'bg-gray-200 text-gray-600' : 'bg-red-100 text-red-600'
                    }`}>
                      {isPastEvent ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {isPastEvent
                          ? 'This event has ended'
                          : salesReason === 'sold_out'
                            ? 'Sold out'
                            : 'Tickets unavailable'}
                      </p>
                      <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                        {isPastEvent
                          ? `Ticket sales closed after ${lastEventDayLabel || 'the event date'}. You can still view event details below.`
                          : salesReason === 'sold_out'
                            ? 'All tickets for this event have been sold. Check back for future events.'
                            : 'Tickets are not available for purchase at this time.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {!salesClosed && (
                  <>
                {hasPackages ? (
                  <TicketPackageSelector
                    event={event}
                    tierQuantities={tierQuantities}
                    onQuantityChange={handleTierQuantityChange}
                    disabled={event.status === 'SOLD_OUT'}
                    compact
                  />
                ) : (
                  <>
                    <div className="flex items-baseline justify-between py-1">
                      <span className="text-sm text-gray-600">Price per ticket</span>
                      <span className="text-xl font-bold text-amber-600">{formatPrice(event.price)}</span>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
                        Quantity
                      </label>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setTicketQuantity(Math.max(1, ticketQuantity - 1))}
                          className="w-9 h-9 rounded-lg border border-gray-300 flex items-center justify-center hover:border-amber-500 hover:bg-amber-50 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                          </svg>
                        </button>
                        <span className="text-lg font-semibold w-10 text-center">{ticketQuantity}</span>
                        <button
                          onClick={() => setTicketQuantity(Math.min(10, ticketQuantity + 1))}
                          className="w-9 h-9 rounded-lg border border-gray-300 flex items-center justify-center hover:border-amber-500 hover:bg-amber-50 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <div className="border-t border-gray-100 pt-2 mt-1">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-medium text-gray-600">Total</span>
                    <span className="text-base font-bold text-amber-600">
                      {formatKesPrice(getOrderTotal())}
                    </span>
                  </div>

                  <button
                    onClick={handleTicketPurchase}
                    disabled={purchasing || event.status === 'SOLD_OUT'}
                    className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ${
                      event.status === 'SOLD_OUT'
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed'
                    }`}
                  >
                    {event.status === 'SOLD_OUT' ? (
                      'Sold Out'
                    ) : purchasing ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Processing...
                      </div>
                    ) : (
                      'Purchase Tickets'
                    )}
                  </button>
                </div>
                  </>
                )}

                {salesClosed && (
                  <div className="flex items-baseline justify-between py-1 text-sm border-b border-gray-100 pb-3">
                    <span className="text-gray-500">
                      {hasPackages ? 'Packages from' : 'Ticket price was'}
                    </span>
                    <span className="font-semibold text-gray-700">
                      {hasPackages
                        ? formatKesPrice(event.startingPrice ?? event.price)
                        : formatPrice(event.price)}
                    </span>
                  </div>
                )}

                {!salesClosed && (
                <div className="bg-slate-50/90 border border-slate-100 rounded-lg p-2.5 mt-1">
                  <div className="flex items-start gap-2">
                    <svg className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <div>
                      <p className="text-[11px] font-medium text-slate-700">Secure checkout</p>
                      <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">Tickets delivered instantly via email with QR codes.</p>
                    </div>
                  </div>
                </div>
                )}

                {!salesClosed && (
                  <p className="text-[10px] text-gray-400 leading-relaxed pt-2">
                    By purchasing tickets, you agree to our{' '}
                    <Link to="/terms-of-service" className="text-gray-500 hover:text-amber-700 underline underline-offset-2">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy-policy" className="text-gray-500 hover:text-amber-700 underline underline-offset-2">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-amber-100 p-3 lg:p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Event QR Code</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Scan to open this event instantly.
                  </p>
                </div>
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100 px-2 py-1 text-[11px] font-medium">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7V4h3M17 4h3v3M20 17v3h-3M7 20H4v-3M5 9h4v4H5V9zm10 0h4v4h-4V9zM5 15h4v4H5v-4zm7-7h1m2 0h1m-4 2h1m2 0h1m-4 2h1m1 1h3m-3 2h1m-1 2h1m2-2h1m-4 2h1m2 0h1" />
                  </svg>
                  Ready
                </span>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="w-36 h-36 rounded-xl border border-gray-200 bg-white flex items-center justify-center p-2 shrink-0">
                  {eventQrDataUrl ? (
                    <img src={eventQrDataUrl} alt={`QR code for ${event.title}`} className="w-full h-full object-contain" />
                  ) : (
                    <div className="text-xs text-gray-400 text-center">Generating QR…</div>
                  )}
                </div>

                <div className="min-w-0 flex-1 w-full">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
                    Short link
                  </p>
                  <p className="text-sm text-gray-900 break-all">{shortEventUrl}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      type="button"
                      onClick={copyToClipboard}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Copy link
                    </button>
                    <button
                      type="button"
                      onClick={downloadQrCode}
                      disabled={!eventQrDataUrl}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-sm font-semibold text-white hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50"
                    >
                      Download QR
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* MOBILE_ABOUT_HOST: after Get Your Tickets - visible below lg */}
            <section
              aria-label="About this event"
              className="block lg:!hidden bg-white rounded-xl shadow-md border border-amber-100 p-4"
            >
              <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">About</h2>
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                {event.description || 'No description available.'}
              </p>

              {(hostDisplay?.name || event.organizer?.name) && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                    Event organizer
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gradient-to-r from-amber-500 to-orange-500 rounded-md flex items-center justify-center text-white font-semibold text-sm shrink-0">
                      {(hostDisplay?.name || event.organizer?.name || 'E').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {hostDisplay?.name || event.organizer?.name}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* Tags */}
            {event.tags && event.tags.length > 0 && (
              <div className="bg-white rounded-xl shadow-md border border-amber-100 p-4 lg:p-5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Tags</h3>
                <div className="flex flex-wrap gap-1.5">
                  {event.tags.map((tag, index) => (
                    <span key={index} className="bg-amber-50 text-amber-800 px-2.5 py-1 rounded-md text-xs font-medium border border-amber-100">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Location map */}
            {event.venue?.coordinates && (
              <div className="bg-white rounded-xl shadow-md border border-amber-100 overflow-hidden">
                <div className="p-4 flex items-center justify-between border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900">Location</h3>
                  <button
                    onClick={() => setShowMap(!showMap)}
                    className="text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors"
                  >
                    {showMap ? 'Hide map' : 'Show map'}
                  </button>
                </div>
                {showMap && (
                  <div className="h-48 bg-gray-50 flex items-center justify-center">
                    <div className="text-center text-sm text-gray-500">
                      <div className="text-3xl mb-1">🗺️</div>
                      <p>Map integration coming soon</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Share This Event</h3>

            <div className="flex flex-col items-center mb-6">
              <div className="w-40 h-40 rounded-xl border border-gray-200 bg-white p-2 flex items-center justify-center">
                {eventQrDataUrl ? (
                  <img src={eventQrDataUrl} alt={`QR code for ${event.title}`} className="w-full h-full object-contain" />
                ) : (
                  <div className="text-xs text-gray-400">Generating QR…</div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-3 text-center">Scan to open this event page directly</p>
              <p className="text-sm text-gray-900 mt-1 break-all text-center">{shortEventUrl}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <button
                onClick={() => handleSocialShare('twitter')}
                className="flex items-center justify-center space-x-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
              >
                <svg className="w-6 h-6 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.902 4.902 0 01-2.228.084 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
                <span className="font-semibold">Twitter</span>
              </button>
              
              <button
                onClick={() => handleSocialShare('facebook')}
                className="flex items-center justify-center space-x-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
              >
                <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="font-semibold">Facebook</span>
              </button>
              
              <button
                onClick={() => handleSocialShare('whatsapp')}
                className="flex items-center justify-center space-x-3 p-4 bg-green-50 hover:bg-green-100 rounded-xl transition-colors"
              >
                <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                <span className="font-semibold">WhatsApp</span>
              </button>
              
              <button
                onClick={() => handleSocialShare('linkedin')}
                className="flex items-center justify-center space-x-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
              >
                <svg className="w-6 h-6 text-blue-700" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                <span className="font-semibold">LinkedIn</span>
              </button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                onClick={downloadQrCode}
                disabled={!eventQrDataUrl}
                className="bg-white border border-amber-200 text-amber-700 py-3 rounded-xl font-semibold hover:bg-amber-50 transition-colors disabled:opacity-50"
              >
                Download QR
              </button>
              <button
                onClick={copyToClipboard}
                className="bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
              >
                Copy Link
              </button>
              <button
                onClick={() => setShowShareModal(false)}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 transition-all duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      </SiteMain>
    </SiteLayout>
  );
};

export default EventDetailsPage;